import { UseFormReturn } from "react-hook-form";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { InlineCemeteryForm } from "../forms/InlineCemeteryForm";
import type { OrderFormData, CemeteryOption, CemeteryFormData } from "../types";

interface CemeterySectionProps {
  form: UseFormReturn<OrderFormData>;
  cemeteries: CemeteryOption[] | undefined;
  isCreating: boolean;
  setIsCreating: (value: boolean) => void;
  formData: CemeteryFormData;
  setFormData: (data: CemeteryFormData) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export function CemeterySection({
  form,
  cemeteries,
  isCreating,
  setIsCreating,
  formData,
  setFormData,
  onSubmit,
  isSubmitting,
}: CemeterySectionProps) {
  const selectedCemeteryId = form.watch("cemetery_id");
  const selectedCemetery = cemeteries?.find(c => c.id === selectedCemeteryId);

  // Update requires_cemetery_approval when cemetery changes
  useEffect(() => {
    if (selectedCemetery) {
      // Only set the default if the field hasn't been manually set yet
      const currentValue = form.getValues("requires_cemetery_approval");
      if (currentValue === undefined) {
        form.setValue("requires_cemetery_approval", selectedCemetery.requires_approval);
      }
    } else {
      form.setValue("requires_cemetery_approval", false);
    }
  }, [selectedCemeteryId, selectedCemetery, form]);

  if (isCreating) {
    return (
      <InlineCemeteryForm
        formData={formData}
        onChange={setFormData}
        onSubmit={onSubmit}
        onCancel={() => setIsCreating(false)}
        isSubmitting={isSubmitting}
      />
    );
  }

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="cemetery_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Hautausmaa</FormLabel>
            <Select
              onValueChange={(value) => {
                field.onChange(value === "none" ? undefined : value);
                // Reset approval checkbox when cemetery changes
                const cemetery = cemeteries?.find(c => c.id === value);
                form.setValue("requires_cemetery_approval", cemetery?.requires_approval ?? false);
              }}
              value={field.value || "none"}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Valitse hautausmaa (valinnainen)" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="none">Ei hautausmaata</SelectItem>
                {cemeteries?.map((cemetery) => (
                  <SelectItem key={cemetery.id} value={cemetery.id}>
                    {cemetery.name} - {cemetery.city}
                    {cemetery.requires_approval && " (hyväksyntä)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {selectedCemeteryId && selectedCemeteryId !== "none" && (
        <FormField
          control={form.control}
          name="requires_cemetery_approval"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  Vaatii seurakunnan hyväksynnän
                </FormLabel>
                <FormDescription>
                  {selectedCemetery?.requires_approval 
                    ? "Hautausmaan oletusasetus: vaaditaan hyväksyntä" 
                    : "Hautausmaan oletusasetus: ei vaadi hyväksyntää"}
                </FormDescription>
              </div>
            </FormItem>
          )}
        />
      )}
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setIsCreating(true)}
        className="w-full"
      >
        <Plus className="mr-2 h-4 w-4" />
        Luo uusi hautausmaa
      </Button>
    </div>
  );
}
